/**
 * BatchGalleryPicker — modal to pick multiple image assets from the user's
 * gallery to feed the batch as sources. Uses checkbox multi-select and
 * confirms with an "Agregar N seleccionadas" button.
 */
import { useEffect, useState } from 'react';
import { X, Check, Loader2, Image as ImageIcon } from 'lucide-react';
import { galleryService } from '../../services/galleryService';
import { useBatchStore } from '../../store/batchStore';
import { toast } from 'sonner';

const BatchGalleryPicker = ({ onClose }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await galleryService.listAssets({ assetType: 'image', limit: 100 });
        if (!cancelled) setAssets(res.assets || []);
      } catch (e) {
        if (!cancelled) toast.error(`No se pudo cargar la galería: ${e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    const chosen = assets.filter((a) => selected.has(a.id));
    if (chosen.length === 0) {
      toast('Seleccioná al menos una imagen.');
      return;
    }
    useBatchStore.getState().addSources(
      chosen.map((a) => ({ url: a.url, name: a.filename || `asset_${a.id.slice(0, 6)}` }))
    );
    toast.success(`${chosen.length} imagen(es) agregadas al lote.`);
    onClose();
  };

  return (
    <div className="batch-gallery-backdrop" onClick={onClose}>
      <div className="batch-gallery-modal" onClick={(e) => e.stopPropagation()} data-testid="batch-gallery-modal">
        <header className="batch-gallery-header">
          <h3><ImageIcon size={16} /> Seleccionar imágenes de la galería</h3>
          <button className="batch-gallery-close" onClick={onClose}><X size={16} /></button>
        </header>

        {loading ? (
          <div className="batch-gallery-loading"><Loader2 className="spin" /> Cargando…</div>
        ) : assets.length === 0 ? (
          <div className="batch-gallery-empty">La galería no tiene imágenes todavía.</div>
        ) : (
          <div className="batch-gallery-grid">
            {assets.map((a) => {
              const isSel = selected.has(a.id);
              return (
                <button
                  key={a.id}
                  className={`batch-gallery-tile ${isSel ? 'selected' : ''}`}
                  onClick={() => toggle(a.id)}
                  data-testid={`batch-gallery-tile-${a.id}`}
                >
                  <img src={a.url} alt={a.filename} loading="lazy" />
                  {isSel && (
                    <span className="batch-gallery-checkmark">
                      <Check size={14} />
                    </span>
                  )}
                  <span className="batch-gallery-tile-name">{a.filename || a.id.slice(0, 6)}</span>
                </button>
              );
            })}
          </div>
        )}

        <footer className="batch-gallery-footer">
          <span className="batch-gallery-count">{selected.size} seleccionada(s)</span>
          <div className="batch-gallery-actions">
            <button onClick={onClose}>Cancelar</button>
            <button className="primary" onClick={handleConfirm} data-testid="batch-gallery-confirm">
              Agregar {selected.size || ''}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default BatchGalleryPicker;
